import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PriorityChip } from '@/lib/referral/receivedEpikrise';
import { placeAnchoredMenu } from '@/components/ui/placeAnchoredMenu';

/** DIS01-M 3.12 / DIS02-M 2.6 (G8): the colour marking, with its meaning as a mouseover/focus tooltip. */
export function EpikrisePriorityChip({ chip }: { chip: PriorityChip }) {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!visible) {
      setBox(null);
      return;
    }
    const place = () => {
      const trigger = wrapRef.current;
      const tip = tipRef.current;
      if (!trigger || !tip) return;
      const rect = trigger.getBoundingClientRect();
      const positioned = placeAnchoredMenu({
        trigger: rect,
        menuWidth: tip.offsetWidth,
        menuHeight: tip.offsetHeight,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        align: 'start',
        gap: 4,
      });
      setBox({ top: positioned.top, left: positioned.left });
    };
    place();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    if (tipRef.current) observer?.observe(tipRef.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [visible, chip.meaning]);

  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  return (
    <>
      <span
        ref={wrapRef}
        className="inline-flex cursor-help rounded focus:outline-none focus-visible:shadow-ring"
        tabIndex={0}
        aria-label={`${chip.label}: ${chip.meaning}`}
        data-testid="epikrise-priority-chip"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${chip.className}`}>{chip.label}</span>
      </span>
      {visible && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={tipRef}
              role="tooltip"
              className="pointer-events-none fixed z-[9999] w-72 rounded-md border border-ds-line bg-ds-card p-2 text-xs font-normal text-ds-text shadow-card"
              style={
                box
                  ? { top: box.top, left: box.left }
                  : { top: 0, left: 0, visibility: 'hidden' as const }
              }
            >
              {chip.meaning}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
